import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import * as sessionNotesService from '../../services/sessionNotes';
import * as franchiseService from '../../services/franchise';
import * as centreService from '../../services/centre';
import * as studentService from '../../services/student';

interface SubjectNote {
  student_id: string;
  student_name: string;
  subject_id: string;
  subject_name: string;
  attended_session: boolean;
  homework_status: string; // 'na', 'completed', 'completed_and_marked', 'not_completed'
  session_note: string;
}

const AddSessionNoteByStudent: React.FC = () => {
  const navigate = useNavigate();
  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);
  
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState<'draft' | 'review_pending' | 'published' | null>(null);
  const [franchises, setFranchises] = useState<any[]>([]);
  const [centres, setCentres] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [subjectNotes, setSubjectNotes] = useState<SubjectNote[]>([]);
  const [subjectNotesErrors, setSubjectNotesErrors] = useState<Record<string, { session_note?: string }>>({});
  const [recentSessionNotes, setRecentSessionNotes] = useState<any[]>([]);

  const validationSchema = Yup.object({
    session_date: Yup.string().required('Session date is required'),
    franchise_id: Yup.string().required('Franchise is required'),
    student_ids: Yup.array().min(1, 'Please select at least one student').required('Students are required'),
  });

  const formik = useFormik({
    initialValues: {
      session_date: '',
      franchise_id: '',
      centre_id: '',
      student_ids: [] as string[]
    },
    validationSchema,
    onSubmit: async () => {
      // This will be called by handleSubmit
    }
  });

  useEffect(() => {
    fetchFranchises();
  }, []);

  useEffect(() => {
    // Auto-select franchise if only one is available
    if (franchises.length === 1 && !formik.values.franchise_id) {
      formik.setFieldValue('franchise_id', franchises[0].id.toString());
      const selectedFranchise = franchises[0];
      setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
    }
  }, [franchises]);

  useEffect(() => {
    // Auto-select centre if only one is available
    if (centres.length === 1 && !formik.values.centre_id) {
      formik.setFieldValue('centre_id', centres[0].id.toString());
    }
  }, [centres]);

  useEffect(() => {
    if (formik.values.franchise_id) {
      fetchCentres(parseInt(formik.values.franchise_id));
      fetchStudents();
      fetchRecentSessionNotes();
      // Clear student selection when dependencies change
      formik.setFieldValue('student_ids', []);
    } else {
      setCentres([]);
      setStudents([]);
      setSubjectNotes([]);
      setRecentSessionNotes([]);
      formik.setFieldValue('centre_id', '');
      formik.setFieldValue('student_ids', []);
    }
  }, [formik.values.franchise_id, formik.values.centre_id, formik.values.session_date]);

  useEffect(() => {
    if (formik.values.student_ids.length > 0 && students.length > 0) {
      const selectedStudents = students.filter(s => 
        formik.values.student_ids.includes(s.id.toString())
      );
      initializeSubjectNotes(selectedStudents);
    } else {
      setSubjectNotes([]);
    }
  }, [formik.values.student_ids, students]);

  const fetchFranchises = async () => {
    try {
      const response = await franchiseService.getAllActiveFranchise();
      if (response?.success) {
        setFranchises(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch franchises');
    }
  };

  const fetchCentres = async (franchiseId: number) => {
    try {
      const response = await centreService.getCentresByFranchiseId(franchiseId);
      if (response?.success) {
        setCentres(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch centres');
    }
  };

  const fetchRecentSessionNotes = async () => {
    if (!formik.values.franchise_id) {
      setRecentSessionNotes([]);
      return;
    }

    try {
      const filters: any = {
        franchiseId: parseInt(formik.values.franchise_id),
        attended: 'yes',
        limit: 10
      };

      if (formik.values.centre_id) {
        filters.centreId = parseInt(formik.values.centre_id);
      }

      const response = await sessionNotesService.getSessionNoteDetails(filters);
      
      if (response?.success) {
        setRecentSessionNotes(response.collections || []);
      }
    } catch (error) {
      // Silently fail for recent notes as it's not critical
      setRecentSessionNotes([]);
    }
  };

  const fetchStudents = async () => {
    if (!formik.values.franchise_id || !formik.values.session_date) {
      setStudents([]);
      return;
    }

    try {
      const params: any = {
        franchise_id: parseInt(formik.values.franchise_id),
        session_date: formik.values.session_date
      };

      if (formik.values.centre_id) {
        params.centre_id = parseInt(formik.values.centre_id);
      }

      const response = await studentService.getStudentsByQuery(params);
      
      if (response?.success) {
        // Transform the response to match our expected format
        const transformedStudents = response.collections.map((student: any) => ({
          id: student.id,
          first_name: student.first_name,
          middle_name: student.middle_name,
          last_name: student.last_name,
          centre_id: student.centre_id,
          subjects: student.subjects.map((subject: any) => ({
            subject_id: subject.id,
            subject_name: subject.name,
            session_date: subject.session_date // Include session_date to filter subjects
          })),
          // Disable student if their session status is completed
          isDisabled: student.student_session_status === 'completed',
          student_session_status: student.student_session_status // not_started | partial | completed
        }));
        
        setStudents(transformedStudents);
      } else {
        toast.error(response?.message || 'Failed to fetch students');
        setStudents([]);
      }
    } catch (error) {
      toast.error('Failed to fetch students');
      setStudents([]);
    }
  };

  const initializeSubjectNotes = (selectedStudents: any[]) => {
    const notes: SubjectNote[] = [];
    
    // For each selected student, create rows only for subjects where session_date is null
    selectedStudents.forEach((student: any) => {
      if (student.subjects && Array.isArray(student.subjects)) {
        student.subjects.forEach((subject: any) => {
          // Only include subjects that don't have a session note yet (session_date is null)
          if (subject.session_date === null || subject.session_date === undefined) {
            notes.push({
              student_id: student.id.toString(),
              student_name: [student.first_name, student.middle_name, student.last_name].filter(Boolean).join(' '),
              subject_id: subject.subject_id.toString(),
              subject_name: subject.subject_name,
              attended_session: true,
              homework_status: 'na',
              session_note: ''
            });
          }
        });
      }
    });
    
    setSubjectNotes(notes);
  };

  const handleFranchiseChange = (option: any) => {
    if (option) {
      formik.setFieldValue('franchise_id', option.id.toString());
      const selectedFranchise = franchises.find(f => f.id === option.id);
      setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
    } else {
      formik.setFieldValue('franchise_id', '');
      setReviewEnabled(false);
    }
  };

  const handleSubjectNoteChange = (studentId: string, subjectId: string, field: string, value: any) => {
    setSubjectNotes(prev =>
      prev.map(note =>
        note.student_id === studentId && note.subject_id === subjectId 
          ? { ...note, [field]: value } 
          : note
      )
    );
    
    // Clear error for session_note when user types
    if (field === 'session_note' && value.trim() !== '') {
      const key = `${studentId}-${subjectId}`;
      setSubjectNotesErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handleMarkAllAttended = (attended: boolean) => {
    setSubjectNotes(prev =>
      prev.map(note => ({
        ...note,
        attended_session: attended,
        homework_status: attended ? note.homework_status : 'na',
        session_note: attended ? note.session_note : ''
      }))
    );
    
    if (!attended) {
      setSubjectNotesErrors({});
    }
  };

  const isAllAttended = subjectNotes.length > 0 && subjectNotes.every(note => note.attended_session);
  const isSomeAttended = subjectNotes.some(note => note.attended_session) && !isAllAttended;

  const validateSubjectNotes = () => {
    const errors: Record<string, { session_note?: string }> = {};
    let hasErrors = false;
    const attendedSubjects = subjectNotes.filter(note => note.attended_session);

    for (const note of attendedSubjects) {
      if (!note.session_note || note.session_note.trim() === '') {
        const key = `${note.student_id}-${note.subject_id}`;
        errors[key] = { session_note: 'Session note is required' };
        hasErrors = true;
      }
    }

    setSubjectNotesErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (status: 'draft' | 'review_pending' | 'published') => {
    // Validate form fields
    const errors = await formik.validateForm();
    formik.setTouched({
      session_date: true,
      franchise_id: true,
      student_ids: true
    });
    
    if (Object.keys(errors).length > 0) {
      // Show specific error messages
      if (errors.session_date) {
        toast.error(errors.session_date);
      } else if (errors.franchise_id) {
        toast.error(errors.franchise_id);
      } else if (errors.student_ids) {
        toast.error(errors.student_ids as string);
      }
      return;
    }

    if (subjectNotes.length === 0) {
      toast.error('No subjects found for the selected students');
      return;
    }

    // Validate subject notes
    if (!validateSubjectNotes()) {
      toast.error('Please fill in session notes for all attended subjects');
      return;
    }

    setLoading(true);
    setSavingStatus(status);
    try {
      // Group by subject_id and aggregate students - only include attended subjects
      const subjectsMap = new Map<string, any[]>();
      
      // Filter to only include attended subjects
      const attendedSubjectNotes = subjectNotes.filter(note => note.attended_session);
      
      // Check if there are any attended subjects
      if (attendedSubjectNotes.length === 0) {
        toast.error('Please mark at least one subject as attended');
        setLoading(false);
        return;
      }
      
      attendedSubjectNotes.forEach(note => {
        if (!subjectsMap.has(note.subject_id)) {
          subjectsMap.set(note.subject_id, []);
        }
        
        subjectsMap.get(note.subject_id)!.push({
          student_id: parseInt(note.student_id),
          attended: 'yes', // Only attended subjects are included
          homework_status: note.homework_status,
          session_note: note.session_note
        });
      });

      // Convert map to subjects array
      const subjects = Array.from(subjectsMap.entries()).map(([subject_id, students]) => ({
        subject_id: parseInt(subject_id),
        students: students
      }));

      const payload = {
        session_date: formik.values.session_date,
        franchise_id: parseInt(formik.values.franchise_id),
        centre_id: formik.values.centre_id ? parseInt(formik.values.centre_id) : '',
        status: status,
        subjects: subjects
      };

      const response = await sessionNotesService.createSessionNote(payload);
      if (response?.success) {
        toast.success('Session note created successfully');
        navigate('/session-notes');
      } else {
        toast.error(response?.message || 'Failed to create session note');
      }
    } catch (error) {
      toast.error('Failed to create session note');
    } finally {
      setLoading(false);
      setSavingStatus(null);
    }
  };

  if (!canAccess?.create) {
    return (
      <div className="">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              You do not have permission to create session notes.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Add Session Note (By Student)</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/session-notes">Session Notes</Link>
                </li>
                <li className="breadcrumb-item active">Add By Student</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Session Details</h3>
              <div className="card-tools">
                <Link to="/session-notes" className="btn btn-sm btn-default">
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to List
                </Link>
              </div>
            </div>
            <div className="card-body">
              {/* Info text for selected franchise and centre */}
              {franchises.length === 1 && centres.length === 1 && formik.values.franchise_id && formik.values.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === formik.values.franchise_id)?.name}
                  <span className="mx-2">•</span>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === formik.values.centre_id)?.name}
                </div>
              )}
              {franchises.length === 1 && centres.length > 1 && formik.values.franchise_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === formik.values.franchise_id)?.name}
                </div>
              )}
              {franchises.length > 1 && centres.length === 1 && formik.values.franchise_id && formik.values.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === formik.values.centre_id)?.name}
                </div>
              )}
              
              <div className="row">
                {franchises.length > 1 && (
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>
                        Franchise <span className="text-danger">*</span>
                      </label>
                      <Select
                        name="franchise_id"
                        placeholder="Search By Franchise"
                        options={franchises}
                        getOptionValue={(option) => `${option['id']}`}
                        getOptionLabel={(option) => option['name']}
                        onChange={(option) => {
                          handleFranchiseChange(option);
                        }}
                        onBlur={() => formik.setFieldTouched('franchise_id', true)}
                        isClearable={true}
                        isSearchable={true}
                        styles={{
                          control: (provided: any, state: any) => ({
                            ...provided,
                            borderColor: formik.touched.franchise_id && formik.errors.franchise_id ? '#dc3545' : provided.borderColor,
                            '&:hover': {
                              borderColor: formik.touched.franchise_id && formik.errors.franchise_id ? '#dc3545' : provided.borderColor
                            }
                          })
                        }}
                      />
                      {formik.touched.franchise_id && formik.errors.franchise_id && (
                        <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                          {formik.errors.franchise_id}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {centres.length > 1 && (
                  <div className={franchises.length > 1 ? "col-md-3" : "col-md-4"}>
                    <div className="form-group">
                      <label>Centre</label>
                      <select
                        name="centre_id"
                        className="form-control"
                        value={formik.values.centre_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!formik.values.franchise_id}
                      >
                        <option value="">All Centres</option>
                        {centres.map(centre => (
                          <option key={centre.id} value={centre.id}>
                            {centre.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-6" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-3" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-4" : "col-md-6"
                }>
                  <div className="form-group">
                    <label>
                      Session Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="session_date"
                      className={`form-control ${formik.touched.session_date && formik.errors.session_date ? 'is-invalid' : ''}`}
                      value={formik.values.session_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      max={new Date().toISOString().split('T')[0]}
                    />
                    {formik.touched.session_date && formik.errors.session_date && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                        {formik.errors.session_date}
                      </div>
                    )}
                  </div>
                </div>

                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-6" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-3" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-4" : "col-md-6"
                }>
                  <div className="form-group">
                    <label>
                      Students <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="student_ids"
                      placeholder="Select Students"
                      value={students.filter(s => formik.values.student_ids.includes(s.id.toString()))}
                      options={students}
                      getOptionValue={(option) => `${option.id}`}
                      getOptionLabel={(option) => {
                        let label = [option.first_name, option.middle_name, option.last_name].filter(Boolean).join(' ');
                        if (option.isDisabled) {
                          label += ' (Notes already added)';
                        }
                        return label;
                      }}
                      onChange={(options) => {
                        const selectedIds = options ? options.map((opt: any) => opt.id.toString()) : [];
                        formik.setFieldValue('student_ids', selectedIds);
                      }}
                      onBlur={() => formik.setFieldTouched('student_ids', true)}
                      isMulti={true}
                      isClearable={true}
                      isSearchable={true}
                      isDisabled={!formik.values.franchise_id || !formik.values.session_date}
                      isOptionDisabled={(option: any) => option.isDisabled}
                      styles={{
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: formik.touched.student_ids && formik.errors.student_ids ? '#dc3545' : provided.borderColor,
                          '&:hover': {
                            borderColor: formik.touched.student_ids && formik.errors.student_ids ? '#dc3545' : provided.borderColor
                          }
                        }),
                        option: (provided: any, state: any) => ({
                          ...provided,
                          opacity: state.data.isDisabled ? 0.7 : 1,
                          color: state.data.isDisabled ? '#6c757d' : provided.color,
                          cursor: state.data.isDisabled ? 'not-allowed' : 'pointer',
                          fontStyle: state.data.isDisabled ? 'italic' : 'normal'
                        })
                      }}
                    />
                    {formik.touched.student_ids && formik.errors.student_ids && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                        {formik.errors.student_ids}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Subject Notes */}
          {subjectNotes.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  Subject Session Notes ({subjectNotes.length} subject{subjectNotes.length !== 1 ? 's' : ''})
                </h3>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>Sr.</th>
                        <th style={{ width: '100px' }} className="text-center">
                          <div className="d-flex align-items-center justify-content-center">
                            <input
                              type="checkbox"
                              checked={isAllAttended}
                              ref={(input) => {
                                if (input) {
                                  input.indeterminate = isSomeAttended;
                                }
                              }}
                              onChange={(e) => handleMarkAllAttended(e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', marginRight: '5px' }}
                              title={isAllAttended ? 'Unmark all' : 'Mark all attended'}
                            />
                            <span>Attended</span>
                          </div>
                        </th>
                        <th style={{ width: '200px' }}>Subject Name</th>
                        <th style={{ width: '180px' }}>Homework</th>
                        <th>Session Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjectNotes.map((note, index) => {
                        // Check if this is the first subject for a new student
                        const isFirstSubjectForStudent = index === 0 || 
                          subjectNotes[index - 1].student_id !== note.student_id;
                        
                        // Count how many subjects this student has
                        const studentSubjectCount = subjectNotes.filter(
                          n => n.student_id === note.student_id
                        ).length;
                        
                        return (
                          <React.Fragment key={`${note.student_id}-${note.subject_id}`}>
                            {/* Student separator row */}
                            {isFirstSubjectForStudent && (
                              <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <td colSpan={5} style={{ padding: '8px 12px', fontWeight: 600, fontSize: '14px', color: '#495057' }}>
                                  <i className="fas fa-user mr-2" style={{ color: '#6c757d' }}></i>
                                  {note.student_name} ({studentSubjectCount} subject{studentSubjectCount !== 1 ? 's' : ''})
                                </td>
                              </tr>
                            )}
                            
                            {/* Subject row */}
                            <tr>
                              <td>{index + 1}</td>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={note.attended_session}
                                  onChange={(e) =>
                                    handleSubjectNoteChange(
                                      note.student_id,
                                      note.subject_id,
                                      'attended_session',
                                      e.target.checked
                                    )
                                  }
                                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                              </td>
                              <td>{note.subject_name}</td>
                              <td>
                                <select
                                  className="form-control form-control-sm"
                                  value={note.homework_status}
                                  onChange={(e) =>
                                    handleSubjectNoteChange(
                                      note.student_id,
                                      note.subject_id,
                                      'homework_status',
                                      e.target.value
                                    )
                                  }
                                  disabled={!note.attended_session}
                                >
                                  <option value="na">N/A</option>
                                  <option value="completed">Completed</option>
                                  <option value="completed_and_marked">Completed and Marked</option>
                                  <option value="not_completed">Not Completed</option>
                                </select>
                              </td>
                              <td>
                                <textarea
                                  className={`form-control form-control-sm ${subjectNotesErrors[`${note.student_id}-${note.subject_id}`]?.session_note ? 'is-invalid' : ''}`}
                                  rows={2}
                                  value={note.session_note}
                                  onChange={(e) =>
                                    handleSubjectNoteChange(
                                      note.student_id,
                                      note.subject_id,
                                      'session_note',
                                      e.target.value
                                    )
                                  }
                                  disabled={!note.attended_session}
                                  placeholder="Enter session note..."
                                />
                                {subjectNotesErrors[`${note.student_id}-${note.subject_id}`]?.session_note && (
                                  <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                                    {subjectNotesErrors[`${note.student_id}-${note.subject_id}`].session_note}
                                  </div>
                                )}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => handleSubmit('draft')}
                  disabled={loading}
                >
                  {loading && savingStatus === 'draft' ? 'Saving...' : 'Save as Draft'}
                </button>
                {reviewEnabled ? (
                  <button
                    type="button"
                    className="btn btn-success mr-2"
                    onClick={() => handleSubmit('review_pending')}
                    disabled={loading}
                  >
                    {loading && savingStatus === 'review_pending' ? 'Submitting...' : 'Submit for Review'}
                  </button>
                ) : (
                  canAccess?.publish && (
                    <button
                      type="button"
                      className="btn btn-primary mr-2"
                      onClick={() => {
                        const attendedCount = subjectNotes.filter(note => note.attended_session).length;
                        
                        confirmAlert({
                          title: 'Confirm Publish',
                          message: 'dummy', // Will be replaced by customUI
                          customUI: ({ onClose }) => {
                            return (
                              <div className="custom-ui" style={{ 
                                padding: '30px', 
                                backgroundColor: 'white', 
                                borderRadius: '10px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                minWidth: '450px'
                              }}>
                                <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: 600, color: '#333' }}>
                                  <i className="fas fa-paper-plane" style={{ color: '#007bff', marginRight: '10px' }}></i>
                                  Confirm Publish
                                </h2>
                                <p style={{ marginBottom: '20px', fontSize: '16px', color: '#666', lineHeight: '1.5' }}>
                                  Are you sure you want to publish these session notes?
                                </p>
                                <div style={{ 
                                  marginBottom: '25px',
                                  padding: '15px',
                                  backgroundColor: '#e7f3ff',
                                  borderLeft: '4px solid #007bff',
                                  borderRadius: '5px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                    <i className="fas fa-info-circle" style={{ color: '#007bff', fontSize: '18px', marginRight: '10px' }}></i>
                                    <span style={{ fontSize: '15px', fontWeight: 500, color: '#004085' }}>
                                      Publishing Information
                                    </span>
                                  </div>
                                  <div style={{ paddingLeft: '28px' }}>
                                    <div style={{ fontSize: '14px', color: '#004085' }}>
                                      <i className="fas fa-check-circle" style={{ color: '#28a745', marginRight: '8px' }}></i>
                                      <strong>{attendedCount}</strong> session note(s) will be published
                                    </div>
                                  </div>
                                </div>
                                <div style={{ 
                                  padding: '12px',
                                  backgroundColor: '#fff3cd',
                                  borderRadius: '5px',
                                  marginBottom: '20px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <i className="fas fa-exclamation-triangle" style={{ color: '#856404', fontSize: '16px', marginRight: '10px', marginTop: '2px' }}></i>
                                    <span style={{ fontSize: '14px', color: '#856404', lineHeight: '1.4' }}>
                                      Once published, these notes will be <strong>visible to students and parents</strong>.
                                    </span>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                  <button
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    style={{ minWidth: '100px' }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                      handleSubmit('published');
                                      onClose();
                                    }}
                                    style={{ minWidth: '120px' }}
                                  >
                                    <i className="fas fa-paper-plane mr-2"></i>
                                    Yes, Publish
                                  </button>
                                </div>
                              </div>
                            );
                          }
                        });
                      }}
                      disabled={loading}
                    >
                      {loading && savingStatus === 'published' ? 'Publishing...' : 'Publish'}
                    </button>
                  )
                )}
                <Link to="/session-notes" className="btn btn-default">
                  Cancel
                </Link>
              </div>
            </div>
          )}
          
          {/* Recent Session Notes */}
          <div className="card mt-4">
            <div className="card-header">
              <h3 className="card-title">Recent Session Notes</h3>
            </div>
            <div className="card-body">
              {recentSessionNotes.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-clipboard-list fa-3x mb-3"></i>
                  <p>No recent session notes found</p>
                </div>
              ) : (
                <div className="timeline">
                  {recentSessionNotes.map((note, index) => (
                    <div key={note.id}>
                      <i className="fas fa-sticky-note bg-info"></i>
                      <div className="timeline-item">
                        <span className="time" style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: '#17a2b8',
                          backgroundColor: '#e7f6f8',
                          padding: '4px 10px',
                          borderRadius: '4px'
                        }}>
                          <i className="fas fa-calendar-alt mr-1"></i>
                          {moment(note.session_date).format('dddd, Do MMMM YYYY')}
                        </span>
                        <h3 className="timeline-header">
                          <strong>{note.student_name}</strong>
                          <span className="mx-2">•</span>
                          <span className="text-muted">{note.subject_name}</span>
                          <span className="mx-2">•</span>
                          <span className={`badge badge-${
                            note.status === 'published' ? 'success' : 
                            note.status === 'review_pending' ? 'warning' : 
                            'secondary'
                          }`}>
                            {note.status === 'published' ? 'Published' : 
                             note.status === 'review_pending' ? 'Review Pending' : 
                             'Draft'}
                          </span>
                        </h3>
                        <div className="timeline-body">
                          <div className="mb-2">
                            <strong>Homework:</strong>
                            <span className={`ml-2 badge ${
                              note.homework_status === 'completed_and_marked' ? 'badge-success' :
                              note.homework_status === 'completed' ? 'badge-info' :
                              note.homework_status === 'not_completed' ? 'badge-danger' :
                              'badge-secondary'
                            }`}>
                              {note.homework_status === 'completed_and_marked' ? 'Completed and Marked' :
                               note.homework_status === 'completed' ? 'Completed' :
                               note.homework_status === 'not_completed' ? 'Not Completed' : 'N/A'}
                            </span>
                          </div>
                          {note.session_note && (
                            <div className="mb-2">
                              <strong>Session Note:</strong>
                              <p className="mt-1 mb-0">{note.session_note}</p>
                            </div>
                          )}
                          <div className="text-muted" style={{ fontSize: '13px' }}>
                            <span>
                              <i className="fas fa-user mr-1"></i>
                              Created by: {note.created_by_name || '--'}
                            </span>
                            {note.reviewed_by_name && note.reviewed_by_name.trim() !== '' && (
                              <>
                                <span className="mx-2">•</span>
                                <span>
                                  <i className="fas fa-user-check mr-1"></i>
                                  Reviewed by: {note.reviewed_by_name}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddSessionNoteByStudent;
